// ---------------------------------------------------------------------------
// PWA_ControlBillet — Main scanner screen
// Camera feed with QR overlay, scan result display, and quick actions.
// ---------------------------------------------------------------------------

import { useRef, useEffect, useState, useCallback } from 'react';
import { useConfigStore } from '@/store/config-store';
import { useAppStore } from '@/store/app-store';
import { useScanStore } from '@/store/scan-store';
import { ScanOverlay } from '@/components/ScanOverlay';
import { ScanResult } from '@/components/ScanResult';
import { feedbackSuccess, feedbackError, feedbackDuplicate } from '@/lib/feedback';
import { SCAN_DEBOUNCE } from '@/lib/constants';
import type { ScanResult as ScanResultType, ValidationRequest } from '@/lib/types';

// ---------------------------------------------------------------------------
// Inline SVG icons for quick actions
// ---------------------------------------------------------------------------

function FlashIcon({ active }: { active: boolean }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill={active ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={active ? 'text-warning' : 'text-white'}
    >
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}

function CameraSwitchIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-white"
    >
      <path d="M11 19H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5" />
      <path d="M13 5h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-5" />
      <polyline points="16 3 18 5 16 7" />
      <polyline points="8 21 6 19 8 17" />
    </svg>
  );
}

function KeyboardIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-white"
    >
      <rect x="2" y="4" width="20" height="16" rx="2" ry="2" />
      <line x1="6" y1="8" x2="6.01" y2="8" />
      <line x1="10" y1="8" x2="10.01" y2="8" />
      <line x1="14" y1="8" x2="14.01" y2="8" />
      <line x1="18" y1="8" x2="18.01" y2="8" />
      <line x1="8" y1="12" x2="8.01" y2="12" />
      <line x1="12" y1="12" x2="12.01" y2="12" />
      <line x1="16" y1="12" x2="16.01" y2="12" />
      <line x1="7" y1="16" x2="17" y2="16" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ScannerScreen() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const lastScanTimeRef = useRef(0);
  const scannerInitRef = useRef(false);

  const configStore = useConfigStore();
  const connectionStatus = useAppStore((s) => s.connectionStatus);
  const showToast = useAppStore((s) => s.showToast);
  const lastScanResult = useScanStore((s) => s.lastScanResult);
  const setLastResult = useScanStore((s) => s.setLastResult);
  const clearLastResult = useScanStore((s) => s.clearLastResult);
  const recordValid = useScanStore((s) => s.recordValid);
  const recordInvalid = useScanStore((s) => s.recordInvalid);
  const recordDuplicate = useScanStore((s) => s.recordDuplicate);

  const [flashEnabled, setFlashEnabled] = useState(false);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const [cameraError, setCameraError] = useState('');

  // ------ Camera start / stop ------

  const startCamera = useCallback(async () => {
    try {
      setCameraError('');

      // Stop previous stream if any.
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError("Impossible d'acceder a la camera. Verifiez les permissions.");
    }
  }, [facingMode]);

  useEffect(() => {
    startCamera();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [startCamera]);

  // ------ QR Scanner (BarcodeDetector API or fallback) ------

  useEffect(() => {
    if (scannerInitRef.current) return;
    scannerInitRef.current = true;

    let animFrameId: number;
    let barcodeDetector: BarcodeDetector | null = null;

    async function initScanner() {
      // Try BarcodeDetector API (Chrome 83+, Android).
      if ('BarcodeDetector' in window) {
        try {
          barcodeDetector = new BarcodeDetector({ formats: ['qr_code'] });
        } catch {
          // BarcodeDetector not available for qr_code.
        }
      }

      if (!barcodeDetector) {
        // Fallback: dynamically import a lightweight library if available.
        // For now, log and rely on manual entry.
        console.warn('BarcodeDetector not available. Use manual entry.');
        return;
      }

      async function scanFrame() {
        if (!videoRef.current || videoRef.current.readyState < 2) {
          animFrameId = requestAnimationFrame(scanFrame);
          return;
        }

        try {
          const barcodes = await barcodeDetector!.detect(videoRef.current);
          if (barcodes.length > 0) {
            const now = Date.now();
            if (now - lastScanTimeRef.current > SCAN_DEBOUNCE) {
              lastScanTimeRef.current = now;
              await handleQRDetected(barcodes[0].rawValue);
            }
          }
        } catch {
          // Detection error — continue scanning.
        }

        animFrameId = requestAnimationFrame(scanFrame);
      }

      animFrameId = requestAnimationFrame(scanFrame);
    }

    initScanner();

    return () => {
      if (animFrameId) cancelAnimationFrame(animFrameId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------ QR Processing pipeline ------

  async function handleQRDetected(data: string) {
    try {
      // Step 1: Import crypto modules.
      const { qrCrypto } = await import('@/crypto/qr-crypto');

      // Step 2: Initialize crypto if needed.
      try {
        const masterKey = await qrCrypto.getMasterKeyFromStorage();
        await qrCrypto.init(masterKey);
      } catch {
        // Already initialized — that's fine.
      }

      // Step 3: Get event config.
      const { eventId, eventDate, vigileId, deviceId } = useConfigStore.getState();

      // Step 4: Decrypt QR.
      const ticketData = await qrCrypto.decryptQR(data, eventId, eventDate);

      if (!ticketData) {
        const result: ScanResultType = {
          success: false,
          type: 'invalid',
          message: 'QR code invalide ou corrompu',
          error: 'DECRYPT_FAILED',
        };
        setLastResult(result);
        recordInvalid();
        feedbackError();
        return;
      }

      // Step 5: Duplicate check.
      let duplicateDetectorInstance: Awaited<typeof import('@/crypto/duplicate-detector')>['duplicateDetector'] | null = null;
      try {
        const mod = await import('@/crypto/duplicate-detector');
        duplicateDetectorInstance = mod.duplicateDetector;
      } catch {
        // Module not yet initialized — skip duplicate check.
      }

      if (duplicateDetectorInstance) {
        const dupResult = await duplicateDetectorInstance.checkDuplicate(ticketData.ticket_id, ticketData.event_id);
        if (dupResult.isDuplicate) {
          const result: ScanResultType = {
            success: false,
            type: 'duplicate',
            message: 'Ce ticket a deja ete scanne',
            ticketData,
            previousScan: dupResult.previousScan as ScanResultType['previousScan'],
          };
          setLastResult(result);
          recordDuplicate();
          feedbackDuplicate();
          return;
        }
      }

      // Step 6: Validate with backend (or queue offline).
      const isOnline = useAppStore.getState().connectionStatus === 'online';

      const validationRequest: ValidationRequest = {
        ticket_id: ticketData.ticket_id,
        event_id: ticketData.event_id,
        customer_name: ticketData.customer_name,
        validated_by: vigileId,
        device_id: deviceId,
        timestamp: new Date().toISOString(),
        checksum: ticketData.checksum,
      };

      let offline = false;

      if (isOnline) {
        try {
          const { agentClient } = await import('@/services/agent-client');
          await agentClient.validateTicket(validationRequest);
        } catch {
          // Backend unavailable — queue offline.
          offline = true;
          try {
            const { offlineQueue } = await import('@/services/offline-queue');
            offlineQueue.enqueue(validationRequest);
          } catch {
            // Offline queue module not yet created.
          }
        }
      } else {
        offline = true;
        try {
          const { offlineQueue } = await import('@/services/offline-queue');
          offlineQueue.enqueue(validationRequest);
        } catch {
          // Offline queue module not yet created.
        }
      }

      // Step 7: Record scan.
      if (duplicateDetectorInstance) {
        await duplicateDetectorInstance.recordScan(
          ticketData.ticket_id,
          ticketData.event_id,
          {
            validatedBy: vigileId,
            deviceId,
            customerName: ticketData.customer_name,
            offline,
          },
        );
      }

      // Step 8: Update stats and show result.
      recordValid();
      feedbackSuccess();

      const result: ScanResultType = {
        success: true,
        type: offline ? 'offline' : 'valid',
        message: offline ? 'Valide (hors ligne)' : 'Billet valide',
        ticketData,
        offline,
      };
      setLastResult(result);
    } catch (err) {
      console.error('Scan processing error:', err);
      const result: ScanResultType = {
        success: false,
        type: 'invalid',
        message: 'Erreur lors du traitement',
        error: 'PROCESSING_ERROR',
      };
      setLastResult(result);
      recordInvalid();
      feedbackError();
    }
  }

  // ------ Flash toggle ------

  function toggleFlash() {
    if (!streamRef.current) return;

    const track = streamRef.current.getVideoTracks()[0];
    if (!track) return;

    try {
      const capabilities = track.getCapabilities() as MediaTrackCapabilities & { torch?: boolean };
      if (!capabilities.torch) {
        showToast('Flash non disponible', 'warning');
        return;
      }

      const next = !flashEnabled;
      track.applyConstraints({
        advanced: [{ torch: next } as MediaTrackConstraintSet],
      });
      setFlashEnabled(next);
    } catch {
      showToast('Flash non supporte', 'warning');
    }
  }

  // ------ Camera switch ------

  function switchCamera() {
    setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
  }

  // ------ Manual entry ------

  function handleManualEntry() {
    const code = window.prompt('Entrez le code du billet manuellement :');
    if (code && code.trim()) {
      handleQRDetected(code.trim());
    }
  }

  // ------ Render ------

  return (
    <div className="relative h-full w-full bg-black pt-14">
      {/* Camera feed */}
      {cameraError ? (
        <div className="flex items-center justify-center h-full px-6">
          <div className="text-center">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="text-danger mx-auto mb-4"
            >
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
            <p className="text-white text-sm mb-4">{cameraError}</p>
            <button
              type="button"
              onClick={startCamera}
              className="btn-primary text-sm"
            >
              Reessayer
            </button>
          </div>
        </div>
      ) : (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}

      {/* Scan overlay */}
      {!cameraError && <ScanOverlay />}

      {/* Scan result card */}
      <ScanResult result={lastScanResult} onDismiss={clearLastResult} />

      {/* Quick action bar */}
      <div className="absolute bottom-20 left-0 right-0 z-20 flex items-center justify-center gap-6 px-4">
        {/* Flash toggle */}
        <button
          type="button"
          onClick={toggleFlash}
          className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-150 active:scale-90 ${
            flashEnabled ? 'bg-warning/30' : 'bg-white/10'
          }`}
          aria-label="Flash"
        >
          <FlashIcon active={flashEnabled} />
        </button>

        {/* Camera switch */}
        <button
          type="button"
          onClick={switchCamera}
          className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center transition-all duration-150 active:scale-90"
          aria-label="Changer de camera"
        >
          <CameraSwitchIcon />
        </button>

        {/* Manual entry */}
        <button
          type="button"
          onClick={handleManualEntry}
          className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center transition-all duration-150 active:scale-90"
          aria-label="Saisie manuelle"
        >
          <KeyboardIcon />
        </button>
      </div>

      {/* Offline indicator overlay */}
      {connectionStatus === 'offline' && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-20">
          <span className="bg-danger/90 text-white text-xs font-semibold px-3 py-1 rounded-full">
            Mode hors ligne
          </span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// BarcodeDetector type declaration (not in default TS lib)
// ---------------------------------------------------------------------------

declare class BarcodeDetector {
  constructor(options?: { formats?: string[] });
  detect(source: HTMLVideoElement | HTMLImageElement | ImageBitmap): Promise<
    Array<{ rawValue: string; format: string; boundingBox: DOMRectReadOnly }>
  >;
}
